import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToast } from '@/components/ui/toast';
import { kb } from '@/lib/api/endpoints';
import { describeFailure } from '@/lib/i18n/errors';

/** Query keys for the knowledge base, kept together like `keys` in queryClient. */
export const kbKeys = {
  topics: ['kb', 'topics'] as const,
  notes: (topicId: string) => ['kb', 'notes', topicId] as const,
  note: (id: string, reveal: boolean) => ['kb', 'note', id, reveal] as const,
  secrets: (topicId: string) => ['kb', 'secrets', topicId] as const,
  search: (q: string) => ['kb', 'search', q] as const,
};

export function useKbTopics() {
  return useQuery({
    queryKey: kbKeys.topics,
    queryFn: () => kb.topics(),
    staleTime: 30_000,
  });
}

export function useKbNotes(topicId: string) {
  return useQuery({
    queryKey: kbKeys.notes(topicId),
    queryFn: () => kb.notes(topicId),
    enabled: Boolean(topicId),
  });
}

export function useKbNote(id: string | null, reveal: boolean) {
  return useQuery({
    queryKey: kbKeys.note(id ?? '', reveal),
    queryFn: () => kb.note(id ?? '', reveal),
    enabled: Boolean(id),
  });
}

export function useKbSecrets(topicId: string) {
  return useQuery({
    queryKey: kbKeys.secrets(topicId),
    // The endpoint 404s when the server has no vault key; surface that as
    // "no secrets UI" rather than an error banner.
    queryFn: () => kb.secrets(topicId),
    enabled: Boolean(topicId),
    retry: false,
  });
}

/** One mutation wrapper: invalidate the KB, toast on failure. */
export function useKbMutation<TVariables, TData>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  onDone?: (data: TData) => void,
) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['kb'] });
      onDone?.(data);
    },
    onError: (error) => toast.error(describeFailure(error, 'common.error')),
  });
}
